<script setup lang="ts">
  // One historic caja-close card: header with close date/total, expandable
  // detail blocks, movements modal and reprint from the stored snapshot.
  import { computed, getCurrentInstance } from 'vue';
  import ModalCajaMovements from '@/components/modals/caja/ModalCajaMovements.vue';
  import V2CajaCanceledDetails from '@/components/v2/stats/cajas/V2CajaCanceledDetails.vue';
  import { useModal } from '@/composition/use-modal';
  import { usePrinting } from '@/composition/use-printing';
  import { getCanceledItemsOmittedUnits } from '@/domain/caja/caja-resume-canceled-items';
  import { ICajaMovement } from '@/interfaces/ICajaMovement';
  import { ICajaResume } from '@/interfaces/ICajaResume';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';

  const props = defineProps<{
    movement: ICajaMovement;
    dense?: boolean;
  }>();

  const restaurantStore = useRestaurantStore();
  const modals = useModal();
  const printing = usePrinting();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const resume = computed(() => props.movement.data as ICajaResume);

  // Details behind the "sin efecto" counters. Cancelled lines are only
  // present on closes stored after the list existed. Frozen JSON is checked
  // for shape: a malformed snapshot must not break the card's render.
  const canceledOrders = computed(() => {
    const details = resume.value?.canceledOrdersDetails;
    return Array.isArray(details) ? details : [];
  });
  const canceledItems = computed(() => {
    const details = resume.value?.canceledItemsDetails;
    return Array.isArray(details) ? details : [];
  });
  const omittedUnits = computed(() =>
    getCanceledItemsOmittedUnits(resume.value)
  );
  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol ?? ''
  );

  function formatPrice(price: number, space = true) {
    return (
      restaurantStore.currency.symbol + (space ? ' ' : '') + displayPrice(price)
    );
  }

  function formatDate(date: string) {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const totalSales = computed(() => {
    if (!resume.value) return 0;

    const by = resume.value.salesByPaymentMethod;
    return (
      (by.cash ?? 0) +
      (by.card ?? 0) +
      (by.online ?? 0) +
      (by.banktransfer ?? 0) +
      (by.qr ?? 0)
    );
  });

  const canViewMovements = computed(() => {
    return (
      typeof props.movement.cajaId === 'number' &&
      typeof props.movement.statusId === 'number'
    );
  });

  function openMovementsModal() {
    if (!canViewMovements.value) return;

    modals.show(
      ModalCajaMovements,
      {
        cajaId: props.movement.cajaId!,
        statusId: props.movement.statusId!,
        cajaName: props.movement.caja?.name,
        openDate: resume.value?.movementsResume?.openDate ?? null,
        closeDate: resume.value?.movementsResume?.closeDate ?? null,
      },
      {
        name: 'caja-movements-modal',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 900,
        classes: 'dialog--height-limit',
      }
    );
  }

  /** Reprints the cierre from the snapshot stored at close time. */
  async function reprintResume() {
    if (!resume.value) return;
    await printing.printCajaResume(resume.value);
  }
</script>

<template>
  <div class="v2-caja-movement" :class="{ 'is-dense': dense }">
    <div class="movement-header">
      <div class="header-left">
        <span class="m-title"
          >{{ movement.caja?.name }} #{{ movement.statusId }}</span
        >
        <span class="m-date">{{
          formatDate(resume.movementsResume.closeDate)
        }}</span>
      </div>
      <div class="header-right">
        <span
          class="m-total"
          v-tooltip="i18nT('v2Stats.cajas.totalSalesTooltip')"
          >{{ formatPrice(totalSales) }}</span
        >
        <button
          type="button"
          class="header-action"
          :disabled="!resume"
          @click="reprintResume"
        >
          {{ $t('v2Stats.cajas.reprint') }}
        </button>
        <button
          type="button"
          class="header-action"
          :disabled="!canViewMovements"
          @click="openMovementsModal"
        >
          {{ $t('v2Stats.cajas.viewMovements') }}
        </button>
      </div>
    </div>

    <div class="movement-body">
      <div class="stats-block">
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.openDate')">
          <v-icon class="row-icon" small>mdi-timer-outline</v-icon>
          <span class="stat-value">{{
            formatDate(resume.movementsResume.openDate)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.closeDate')">
          <v-icon class="row-icon" small>mdi-timer-off-outline</v-icon>
          <span class="stat-value">{{
            formatDate(resume.movementsResume.closeDate)
          }}</span>
        </div>
      </div>
      <div class="stats-separator"></div>
      <div class="stats-block">
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.cashOnOpen')">
          <v-icon class="row-icon" small>mdi-cash</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.movementsResume.cashOnOpen)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.withdrawals')">
          <v-icon class="row-icon" small>mdi-cash-remove</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.movementsResume.withdrawals)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.deposits')">
          <v-icon class="row-icon" small>mdi-cash-plus</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.movementsResume.deposits)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.cashOnClose')">
          <v-icon class="row-icon" small>mdi-cash-register</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.movementsResume.cashOnClose ?? 0)
          }}</span>
        </div>
      </div>
      <div class="stats-separator"></div>
      <div class="stats-block">
        <div class="stat-row" v-tooltip="i18nT('v2Stats.series.cash')">
          <v-icon class="row-icon" small>mdi-cash</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.cash)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.series.card')">
          <v-icon class="row-icon" small>mdi-credit-card</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.card)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.series.online')">
          <v-icon class="row-icon" small>mdi-web</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.online)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.series.banktransfer')">
          <v-icon class="row-icon" small>mdi-bank-transfer</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.banktransfer ?? 0)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.series.qr')">
          <v-icon class="row-icon" small>mdi-qrcode</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.qr)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.series.invitation')">
          <v-icon class="row-icon" small>mdi-account-multiple</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.invitation)
          }}</span>
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.series.employee')">
          <v-icon class="row-icon" small>mdi-account-cash</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.employee)
          }}</span>
        </div>
        <div
          v-if="resume.salesByPaymentMethod.club"
          class="stat-row"
          v-tooltip="i18nT('v2Stats.cajas.clubBalance')"
        >
          <v-icon class="row-icon" small>mdi-wallet</v-icon>
          <span class="stat-value">{{
            formatPrice(resume.salesByPaymentMethod.club)
          }}</span>
        </div>
      </div>
      <div class="stats-separator"></div>
      <div class="stats-block">
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.rejected')">
          <v-icon class="row-icon" small>mdi-cancel</v-icon>
          <span class="stat-value"
            >{{ resume.ordersTotals.rejected }} ({{
              formatPrice(resume.ordersTotals.rejectedTotal, false)
            }})</span
          >
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.canceled')">
          <v-icon class="row-icon" small>mdi-delete-outline</v-icon>
          <span class="stat-value"
            >{{ resume.ordersTotals.canceled }} ({{
              formatPrice(resume.ordersTotals.canceledTotal, false)
            }})</span
          >
        </div>
        <div class="stat-row" v-tooltip="i18nT('v2Stats.cajas.canceledItems')">
          <v-icon class="row-icon" small>mdi-close-box-multiple-outline</v-icon>
          <span class="stat-value"
            >{{ resume.ordersTotals.canceledItems }} ({{
              formatPrice(resume.ordersTotals.canceledItemsTotal, false)
            }})</span
          >
        </div>
        <div
          v-if="typeof resume.ordersTotals.discounted === 'number'"
          class="stat-row"
          v-tooltip="i18nT('v2Stats.cajas.discounted')"
        >
          <v-icon class="row-icon" small>mdi-sale</v-icon>
          <span class="stat-value"
            >{{ resume.ordersTotals.discounted }} ({{
              formatPrice(resume.ordersTotals.discountedTotal, false)
            }})</span
          >
        </div>
      </div>
    </div>

    <V2CajaCanceledDetails
      v-if="canceledOrders.length || canceledItems.length"
      class="movement-details"
      :orders="canceledOrders"
      :items="canceledItems"
      :omitted-units="omittedUnits"
      :currency-symbol="currencySymbol"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-caja-movement {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;

    .movement-header {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-px-4 tw-py-2.5;

      .header-left {
        @apply tw-mr-3 tw-flex tw-flex-wrap tw-items-baseline;

        .m-title {
          @apply tw-mr-2 tw-text-base tw-font-semibold tw-text-neutral-900;
        }

        .m-date {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }

      .header-right {
        @apply tw-flex tw-flex-wrap tw-items-center;

        .m-total {
          @apply tw-mr-2 tw-text-base tw-font-semibold tw-text-neutral-900;
        }

        .header-action {
          // margins instead of flex gap: unsupported on Chrome <84.
          @apply tw-ml-1.5 tw-h-11 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors hover:tw-bg-neutral-50;

          &:disabled {
            @apply tw-cursor-not-allowed tw-opacity-40;
          }

          &:focus-visible {
            @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
          }
        }
      }
    }

    .movement-body {
      @apply tw-flex tw-flex-col tw-px-4 tw-py-3;
      @apply sm:tw-flex-row sm:tw-items-stretch sm:tw-justify-between;

      .stats-separator {
        @apply tw-my-2 tw-border-0 tw-border-b tw-border-dashed tw-border-neutral-200;
        @apply sm:tw-mx-3 sm:tw-my-0 sm:tw-border-b-0 sm:tw-border-l;
      }

      .stats-block {
        @apply tw-flex tw-flex-col tw-items-end;
        @apply sm:tw-items-start;

        & > * + * {
          @apply tw-mt-0.5;
        }

        .stat-row {
          @apply tw-flex tw-items-center;

          .row-icon {
            @apply tw-mr-1 tw-text-neutral-400;
          }

          .stat-value {
            @apply tw-text-sm tw-text-neutral-600;
          }
        }
      }
    }

    &.is-dense {
      .movement-body,
      .movement-details {
        @apply tw-hidden;
      }
    }
  }
</style>
